import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { StorageService } from '../storage/storage.service';
import {
  BehaviorSubject,
  catchError,
  map,
  Observable,
  of,
  switchMap,
  tap,
} from 'rxjs';
import { Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { UsernameType } from '../../model/enum/auth';
import { CataloguesService } from '../catalogues/catalogues.service';
import { LOCAL_CART } from '../../consts/course';
import { KINESCOPE_STORAGE_KEY } from '../../../shared/components/non-base/video-player/kinoscope-player/kinescope.const';
import { UsersService } from '../users/users.service';
import {
  AuthRequest,
  AuthResponse,
  RecoverPassword,
  RegistrationResponse,
} from '../../model/interface/auth';
import {
  CuratorUser,
  MethodistUser,
  StudentUser,
} from '../../model/interface/users';
import { BaseRoutes, CommonRoutes } from '../../model/enum/routes.enum';
import { Roles } from '../../model/enum/roles.enum';
import { Keys } from '../../model/enum/localStorage';
import { WebViewService } from '../webview/webview.service';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  public _isAuthenticated$: BehaviorSubject<boolean> =
    new BehaviorSubject<boolean>(!!localStorage.getItem('token') || false);

  constructor(
    private httpClient: HttpClient,
    private storageService: StorageService,
    private router: Router,
    private toastrService: ToastrService,
    private cataloguesService: CataloguesService,
    private readonly usersService: UsersService,
    private readonly webViewService: WebViewService
  ) {}

  public signIn(data: AuthRequest): Observable<AuthResponse | boolean> {
    return this.httpClient.post<AuthResponse>(`/v1/auth/signin`, data).pipe(
      tap(res => {
        this.afterSignIn(res);
        localStorage.setItem(LOCAL_CART, '[]');
        this.cataloguesService.cartItems$.next([]);
      }),
      map(res => {
        return res;
      }),
      catchError(error => {
        console.error(error);
        return of(false);
      })
    );
  }

  public signInNoToken(data: AuthRequest): Observable<AuthResponse | boolean> {
    return this.httpClient
      .post<AuthResponse>(`/v1/auth/signintoken`, data)
      .pipe(
        tap(res => {
          this.afterSignIn(res);
        }),
        map(res => {
          return res;
        }),
        catchError(error => {
          console.error(error);
          return of(false);
        })
      );
  }

  public signUp(
    data: StudentUser | MethodistUser
  ): Observable<RegistrationResponse | boolean> {
    return this.httpClient
      .post<RegistrationResponse>(`/v1/auth/signup`, data)
      .pipe(
        tap(() => {
          this.router.navigate([CommonRoutes.Auth, CommonRoutes.Authorization]);
        }),
        catchError(error => {
          this.toastrService.error(error.error.messages[0], 'Қате');
          return of(false);
        })
      );
  }

  public logout(): void {
    this.httpClient.delete<void>(`/v1/auth/signout`).subscribe();
    this._isAuthenticated$.next(false);
    this.storageService.removeToken();
    this.storageService.removeFcmToken();
    this.storageService.removeRefreshToken();
    this.webViewService.onLogOut(false);
    localStorage.setItem(LOCAL_CART, '[]');
    this.cataloguesService.cartItems$.next([]);
    localStorage.setItem('activeCourses', JSON.stringify([]));

    this.router.navigate([BaseRoutes.LANDING]).then(() => {
      this.usersService.resetUserProfile();
    });

    localStorage.removeItem('username');
    localStorage.removeItem('email');
    this.clearKinescopeUserData();
  }

  public navigateToErrorPage(): void {
    this.router.navigate(['/system-error']).then();
  }

  public sendEmailToRecoverPassword(
    username: string,
    type: UsernameType,
    token?: string
  ): Observable<RecoverPassword> {
    const data: RecoverPassword = {
      token: token ?? '',
    };

    if (type === UsernameType.EMAIL) {
      data.username = username;
    }
    if (type === UsernameType.SMS) {
      data.phoneNumber = '+7' + username;
    }

    return this.httpClient
      .post<RecoverPassword>(`/v1/auth/recoverpassword/token`, data)
      .pipe(
        map(res => {
          return res;
        })
      );
  }

  private setRole(role: Roles): void {
    localStorage.setItem(Keys.Role, role);
  }

  public getRole(): Roles {
    return localStorage.getItem(Keys.Role) as Roles;
  }

  public refreshToken(
    token: string,
    fcmToken?: string
  ): Observable<AuthResponse> {
    const payload: { refreshToken: string; fcmToken?: string } = {
      refreshToken: token,
    };

    if (fcmToken) {
      payload.fcmToken = fcmToken;
    }

    return this.httpClient
      .post<AuthResponse>(`/v1/auth/refreshtoken`, payload)
      .pipe(
        map(response => {
          this.storageService.setToken(response.token);
          this.storageService.setRefreshToken(response.refreshToken);
          return response;
        })
      );
  }

  public updateFcmToken(fcmToken: string): Observable<void> {
    return this.httpClient.post<void>(`/v1/auth/fcm-token`, { fcmToken });
  }

  public recoverPassword(data: RecoverPassword): Observable<string> {
    return this.httpClient.post<string>(`/v1/auth/recoverpassword`, data);
  }

  public shortUrl(id: string): Observable<{ link: string }> {
    return this.httpClient.get<{ link: string }>(`/v1/shorturls/${id}`);
  }

  public switchToTraining(): Observable<AuthResponse> {
    return this.httpClient
      .post<AuthResponse>(`/v1/auth/switchtotraining`, {})
      .pipe(
        switchMap(res => this.refreshToken(res.refreshToken)),
        tap(res => {
          this.afterSignIn(res);
        })
      );
  }

  public afterSignIn(res: AuthResponse): void {
    this.storageService.setToken(res.token);
    this.storageService.setRefreshToken(res.refreshToken);
    this.setRole(res.roles[0]);
    this._isAuthenticated$.next(true);
  }

  private clearKinescopeUserData(): void {
    try {
      const prefix = `${KINESCOPE_STORAGE_KEY}:`;
      for (let i = sessionStorage.length - 1; i >= 0; i--) {
        const k = sessionStorage.key(i);
        if (k && k.startsWith(prefix)) sessionStorage.removeItem(k);
      }
    } catch {}
  }
}
